import { useMemo, useState } from "react";
import {
  Award,
  Calendar,
  Check,
  CheckCircle,
  Code,
  Info,
  LogOut,
  Mail,
  Palette,
  Pencil,
  Star,
  Trophy,
  Users,
} from "lucide-react";

import Avatar from "../components/Avatar";
import BottomNav from "../components/BottomNav";
import BottomSheet from "../components/BottomSheet";
import { TYPE_ORDER, TYPE_STYLES } from "../utils/constants";
import { AVATARS } from "../utils/avatars";
import { averageRatingOutOfTen, summarize } from "../utils/helpers";
import { USERNAME_MAX, validateUsername } from "../hooks/useProfile";
import { SHADOW, TOKENS as T, inputStyle } from "../styles/styles";

const APP_VERSION = "0.1.0";

export default function ProfilePage({
  activeGroup,
  groups = [],
  entries,
  currentUser,
  profile,
  onSaveProfile,
  onLogout,
  onNavigate,
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [avatarId, setAvatarId] = useState(null);
  const [error, setError] = useState("");

  const summary = useMemo(() => summarize(entries), [entries]);

  const averageRating = useMemo(() => {
    const average = averageRatingOutOfTen(entries);

    return average === null ? null : average.toFixed(1);
  }, [entries]);

  // The type this user tracks most in the current group.
  const favouriteType = useMemo(() => {
    let best = null;
    let bestCount = 0;

    TYPE_ORDER.forEach((type) => {
      const count = entries.filter((entry) => entry.type === type).length;

      if (count > bestCount) {
        best = type;
        bestCount = count;
      }
    });

    return best;
  }, [entries]);

  const memberSince = useMemo(() => {
    const created = currentUser?.metadata?.creationTime;

    if (!created) return "—";

    return new Date(created).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  }, [currentUser]);

  const displayName = profile?.displayName || currentUser?.email || "—";

  function startEditing() {
    setName(profile?.displayName || "");
    setAvatarId(profile?.avatarId || AVATARS[0].id);
    setError("");
    setEditing(true);
  }

  async function handleSave() {
    const problem = validateUsername(name);

    if (problem) {
      setError(problem);
      return;
    }

    try {
      await onSaveProfile({ displayName: name, avatarId });
      setEditing(false);
    } catch (saveError) {
      console.error("Could not save profile:", saveError);
      setError("That didn't save. Give it another go.");
    }
  }

  const quickStats = [
    { label: "Groups", value: groups.length, Icon: Users, color: "#0077FF" },
    {
      label: "Tracked",
      value: summary.total,
      Icon: Award,
      color: "#7C3AED",
    },
    {
      label: "Completed",
      value: summary.completed,
      Icon: CheckCircle,
      color: "#059669",
    },
    {
      label: "Avg rating",
      value: averageRating === null ? "—" : averageRating,
      Icon: Star,
      color: "#D97706",
    },
  ];

  const accountRows = [
    { label: "Email", value: currentUser?.email || "—", Icon: Mail },
    { label: "Member since", value: memberSince, Icon: Calendar },
    {
      label: "Current group",
      value: activeGroup?.name || "None yet",
      Icon: Users,
    },
    {
      label: "Favourite",
      value: favouriteType ? TYPE_STYLES[favouriteType].label : "—",
      Icon: Star,
    },
  ];

  const appRows = [
    { label: "Version", value: APP_VERSION, Icon: Info },
    { label: "Theme", value: "Light", Icon: Palette },
    { label: "Build", value: "Contest build", Icon: Trophy },
  ];

  return (
    <div style={pageStyle}>
      <div style={headerShellStyle}>
        <h1 style={screenTitleStyle}>Profile</h1>
      </div>

      <div style={listShellStyle} className="scrollbar-hide">
        <div style={heroCardStyle}>
          <Avatar
            avatarId={profile?.avatarId}
            fallbackKey={currentUser?.uid}
            initial={displayName[0]}
            size={72}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={heroNameStyle}>{displayName}</h2>
            <p style={heroEmailStyle}>{currentUser?.email || "—"}</p>
          </div>

          <button
            type="button"
            onClick={startEditing}
            aria-label="Edit profile"
            style={editButtonStyle}
          >
            <Pencil size={18} color={T.primary} />
          </button>
        </div>

        <div style={statsGridStyle}>
          {quickStats.map(({ label, value, Icon, color }) => (
            <div key={label} style={statCardStyle}>
              <div style={{ ...statIconBoxStyle, background: `${color}15` }}>
                <Icon size={20} color={color} />
              </div>
              <p style={statValueStyle}>{value}</p>
              <p style={statLabelStyle}>{label}</p>
            </div>
          ))}
        </div>

        <div style={panelStyle}>
          <p style={panelLabelStyle}>Account</p>

          <div style={rowListStyle}>
            {accountRows.map(({ label, value, Icon }) => (
              <div key={label} style={infoRowStyle}>
                <div style={rowIconStyle}>
                  <Icon size={18} color={T.primary} />
                </div>
                <span style={rowLabelStyle}>{label}</span>
                <span style={rowValueStyle}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={panelStyle}>
          <p style={panelLabelStyle}>App</p>

          <div style={rowListStyle}>
            {appRows.map(({ label, value, Icon }) => (
              <div key={label} style={infoRowStyle}>
                <div style={rowIconStyle}>
                  <Icon size={18} color={T.primary} />
                </div>
                <span style={rowLabelStyle}>{label}</span>
                <span style={rowValueStyle}>{value}</span>
              </div>
            ))}

            <div style={infoRowStyle}>
              <div style={rowIconStyle}>
                <Code size={18} color={T.primary} />
              </div>
              <span style={rowLabelStyle}>Source</span>
              <span style={soonBadgeStyle}>Soon</span>
            </div>
          </div>
        </div>

        <button type="button" onClick={onLogout} style={logoutButtonStyle}>
          <LogOut size={18} color={T.destructive} />
          Log out
        </button>
      </div>

      <BottomNav active="profile" onNavigate={onNavigate} />

      {editing && (
        <BottomSheet onClose={() => setEditing(false)}>
          <h2 style={sheetTitleStyle}>Edit profile</h2>
          <p style={sheetSubtitleStyle}>This is how your group sees you.</p>

          <p style={{ ...panelLabelStyle, marginTop: 20 }}>Your name</p>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleSave()}
            maxLength={USERNAME_MAX}
            placeholder="e.g. Affana"
            style={inputStyle}
          />

          <p style={{ ...panelLabelStyle, marginTop: 20 }}>Your avatar</p>
          <div style={galleryStyle}>
            {AVATARS.map((avatar) => {
              const selected = avatar.id === avatarId;

              return (
                <button
                  key={avatar.id}
                  type="button"
                  onClick={() => setAvatarId(avatar.id)}
                  aria-label={avatar.name}
                  style={{
                    ...avatarButtonStyle,
                    border: selected
                      ? `2px solid ${T.primary}`
                      : "2px solid transparent",
                    background: selected
                      ? "rgba(0,119,255,0.06)"
                      : "transparent",
                  }}
                >
                  <Avatar avatarId={avatar.id} size={52} />

                  {selected && (
                    <span style={checkStyle}>
                      <Check size={11} color="#fff" strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {error && <p style={errorTextStyle}>{error}</p>}

          <button type="button" onClick={handleSave} style={sheetSubmitStyle}>
            Save
          </button>
        </BottomSheet>
      )}
    </div>
  );
}

const pageStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: T.background,
  color: T.foreground,
};

const headerShellStyle = {
  padding: "8px 20px 12px",
  flexShrink: 0,
};

const screenTitleStyle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.15,
  color: T.foreground,
};

const listShellStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "8px 20px 150px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const heroCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  background: T.card,
  borderRadius: 24,
  padding: 20,
  boxShadow: SHADOW.card,
};

const heroNameStyle = {
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  lineHeight: 1.25,
  color: T.foreground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const heroEmailStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const editButtonStyle = {
  width: 44,
  height: 44,
  borderRadius: 16,
  border: "none",
  background: "rgba(0,119,255,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const statCardStyle = {
  background: T.card,
  borderRadius: 24,
  padding: 16,
  boxShadow: SHADOW.card,
};

const statIconBoxStyle = {
  width: 36,
  height: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const statValueStyle = {
  margin: 0,
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1,
  color: T.foreground,
};

const statLabelStyle = {
  margin: "6px 0 0",
  fontSize: 12,
  fontWeight: 600,
  color: T.mutedForeground,
};

const panelStyle = {
  background: T.card,
  borderRadius: 24,
  padding: "16px 20px",
  boxShadow: SHADOW.card,
};

const panelLabelStyle = {
  margin: "0 0 8px",
  fontSize: 10,
  fontWeight: 600,
  color: T.mutedForeground,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const rowListStyle = {
  display: "flex",
  flexDirection: "column",
};

const infoRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  minHeight: 52,
  borderTop: `1px solid ${T.border}`,
};

const rowIconStyle = {
  width: 36,
  height: 36,
  borderRadius: 12,
  background: "rgba(0,119,255,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const rowLabelStyle = {
  flex: 1,
  fontSize: 13,
  fontWeight: 500,
  color: T.mutedForeground,
};

const rowValueStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: T.foreground,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "55%",
  textAlign: "right",
};

const soonBadgeStyle = {
  padding: "2px 8px",
  borderRadius: 999,
  background: T.muted,
  color: T.mutedForeground,
  fontSize: 10,
  fontWeight: 700,
};

const logoutButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  width: "100%",
  minHeight: 52,
  padding: "14px 16px",
  borderRadius: 20,
  border: "none",
  background: "rgba(229,55,58,0.08)",
  color: T.destructive,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const sheetTitleStyle = {
  margin: "4px 0 4px",
  fontSize: 20,
  fontWeight: 700,
  color: T.foreground,
};

const sheetSubtitleStyle = {
  margin: 0,
  fontSize: 13,
  color: T.mutedForeground,
};

const galleryStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "space-between",
};

const avatarButtonStyle = {
  position: "relative",
  padding: 4,
  borderRadius: 20,
  cursor: "pointer",
  lineHeight: 0,
  minHeight: 60,
};

const checkStyle = {
  position: "absolute",
  right: 2,
  bottom: 2,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: T.primary,
  border: `2px solid ${T.card}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const errorTextStyle = {
  margin: "16px 0 0",
  fontSize: 13,
  fontWeight: 600,
  color: T.destructive,
};

const sheetSubmitStyle = {
  width: "100%",
  marginTop: 20,
  minHeight: 48,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  background: T.primary,
  color: T.primaryForeground,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
